import React from "react";
import styled, { keyframes } from "styled-components";
import { COLORS } from "../../../helpers/designConstants";
import { useTimeAgo } from "../../../helpers/useTimeAgo";

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: ${props => props.bgColor};
  border: 1px solid ${props => props.borderColor};
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    border-radius: 6px;
  }
`;

const StatusDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.color};
  animation: ${props => props.pulse ? pulse : 'none'} 2s infinite;
`;

const StatusText = styled.span`
  font-weight: 600;
  color: ${props => props.color};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${COLORS.border};
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 6px 0;
  }
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: ${COLORS.textSecondary};
`;

const InfoValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.textPrimary};
`;

const LoadingPlaceholder = styled.div`
  height: 20px;
  background-color: ${COLORS.border};
  border-radius: 4px;
  animation: ${pulse} 1.5s infinite;
`;

const ParserStatusIndicator = ({ status, isLoading }) => {
  const currentExecutionTimeAgo = useTimeAgo(status?.currentExecution?.startTime);

  if (isLoading) {
    return (
      <StatusContainer>
        <LoadingPlaceholder />
        <LoadingPlaceholder style={{ height: '16px' }} />
        <LoadingPlaceholder style={{ height: '16px' }} />
      </StatusContainer>
    );
  }

  const getStatusConfig = () => {
    if (status?.isRunning) {
      return {
        color: COLORS.warning,
        bgColor: "#fef3c7",
        borderColor: "#f59e0b",
        text: "Running",
        pulse: true
      };
    }
    
    return {
      color: COLORS.success,
      bgColor: "#d1fae5",
      borderColor: "#10b981",
      text: "Idle",
      pulse: false
    };
  };

  const statusConfig = getStatusConfig();

  return (
    <StatusContainer>
      <StatusBadge 
        bgColor={statusConfig.bgColor} 
        borderColor={statusConfig.borderColor}
      >
        <StatusDot 
          color={statusConfig.color} 
          pulse={statusConfig.pulse} 
        />
        <StatusText color={statusConfig.color}>
          {statusConfig.text}
        </StatusText>
      </StatusBadge>

      {status?.currentExecution && (
        <div>
          <InfoRow>
            <InfoLabel>Current Execution</InfoLabel>
            <InfoValue>{status.currentExecution.id.split('_').pop()}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Started</InfoLabel>
            <InfoValue>{currentExecutionTimeAgo}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Trigger</InfoLabel>
            <InfoValue style={{ textTransform: 'capitalize' }}>
              {status.currentExecution.trigger}
            </InfoValue>
          </InfoRow>
        </div>
      )}

      {!status?.isRunning && (
        <div>
          <InfoRow>
            <InfoLabel>Status</InfoLabel>
            <InfoValue>Ready for execution</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Next Scheduled</InfoLabel>
            <InfoValue>Every 30 minutes</InfoValue>
          </InfoRow>
        </div>
      )}

      <InfoRow>
        <InfoLabel>Last Updated</InfoLabel>
        <InfoValue>
          {status?.lastUpdated 
            ? new Date(status.lastUpdated).toLocaleTimeString()
            : 'Never'
          }
        </InfoValue>
      </InfoRow>
    </StatusContainer>
  );
};

export default ParserStatusIndicator;